import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ClipboardList, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * CheckinQuestionsBuilder — coach-side settings widget for configuring the
 * custom prompts that will appear on every one of their clients' weekly
 * check-in forms.  Up to 12 prompts, saved on the coach's user doc.
 */
export default function CheckinQuestionsBuilder() {
  const [questions, setQuestions] = useState([""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/checkins/coach-questions")
      .then((r) => {
        const qs = r.data?.questions || [];
        setQuestions(qs.length ? qs : [""]);
      })
      .catch(() => setQuestions([""]))
      .finally(() => setLoading(false));
  }, []);

  const update = (i, v) => setQuestions(questions.map((q, idx) => (idx === i ? v : q)));
  const remove = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const add = () => {
    if (questions.length >= 12) return toast.error("Maximum 12 questions");
    setQuestions([...questions, ""]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/checkins/coach-questions", { questions });
      const saved = r.data?.questions || [];
      setQuestions(saved.length ? saved : [""]);
      toast.success(saved.length ? `${saved.length} question${saved.length === 1 ? "" : "s"} saved` : "Cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="obs-card" data-testid="coach-checkin-questions">
      <div className="flex items-center gap-3 mb-3">
        <ClipboardList className="h-5 w-5 text-blaze" />
        <div className="flex-1">
          <div className="overline">Check-in prompts</div>
          <h3 className="font-heading text-2xl uppercase">What to ask your clients</h3>
        </div>
      </div>
      <p className="text-xs text-zinc-400 mb-3">
        These free-text prompts appear on every client&apos;s weekly check-in form. Answers land in
        the client&apos;s check-in list once submitted. Up to 12.
      </p>
      <div className="space-y-2">
        {questions.map((q, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="font-data text-blaze text-sm mt-2 shrink-0">{i + 1}.</span>
            <textarea
              value={q}
              onChange={(e) => update(i, e.target.value)}
              rows={1}
              placeholder="e.g. Did you hit all your workouts this week? What got in the way?"
              className="flex-1 bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm resize-none"
              data-testid={`coach-q-input-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={questions.length === 1 && !q}
              className="p-2 text-zinc-500 hover:text-blaze disabled:opacity-30"
              aria-label="Remove"
              data-testid={`coach-q-remove-${i}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={add}
          className="ghost-btn px-3 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
          data-testid="coach-q-add"
        >
          <Plus className="h-3 w-3" /> Add prompt
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="blaze-btn px-4 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="coach-q-save"
        >
          <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save prompts"}
        </button>
      </div>
    </div>
  );
}
